import classNames from 'classnames';
import React, { FC, ReactNode, useState } from 'react';

import { NativeProps, withNativeProps } from '../utils/native-props';
import { usePropsValue } from '../utils/use-props-value';
import { mergeProps } from '../utils/with-default-props';

import styles from './switch.module.scss';

export type SwitchProps = {
  loading?: boolean;
  disabled?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  beforeChange?: (val: boolean) => Promise<void>;
  onChange?: (checked: boolean) => void;
  checkedText?: ReactNode;
  uncheckedText?: ReactNode;
} & NativeProps<'--checked-color' | '--width' | '--height' | '--border-width'>;

const defaultProps = {
  defaultChecked: false,
};

export const Switch: FC<SwitchProps> = (p) => {
  const props = mergeProps(defaultProps, p);
  const disabled = props.disabled || props.loading || false;
  const [changing, setChanging] = useState(false);

  const [checked, setChecked] = usePropsValue({
    value: props.checked,
    defaultValue: props.defaultChecked,
    onChange: props.onChange,
  });

  async function onClick() {
    if (disabled || props.loading || changing) {
      return;
    }
    const nextChecked = !checked;
    if (props.beforeChange) {
      setChanging(true);
      try {
        await props.beforeChange(nextChecked);
        setChecked(nextChecked);
        setChanging(false);
      } catch (e) {
        setChanging(false);
        throw e;
      }
    } else {
      setChecked(nextChecked);
    }
  }

  return withNativeProps(
    props,
    <div
      onClick={onClick}
      className={classNames(styles.switch, {
        [styles.switch_checked]: checked,
        [styles.switch_disabled]: disabled || changing,
      })}
    >
      <div className={styles.switch_checkbox}>
        <div className={styles.switch_handle} />
        <div className={styles.switch_inner}>{checked ? props.checkedText : props.uncheckedText}</div>
      </div>
    </div>
  );
};
